import React from 'react';
import { useTranslation } from 'react-i18next';
import { ProductItem } from '../../types/api';

interface ProductSelectorProps {
  selectedProductItem: number | null;
  onProductItemSelect: (productItemId: number) => void;
  productItems: ProductItem[];
}

const ProductSelector: React.FC<ProductSelectorProps> = ({
  selectedProductItem,
  onProductItemSelect,
  productItems,
}) => {
  const { t } = useTranslation('checkout');
  // Fallback icon for broken/missing images
  const fallbackIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Crect width='48' height='48' rx='8' fill='%23f3f4f6'/%3E%3Cpath d='M24 18c-1.65 0-3 1.35-3 3v3h6v-3c0-1.65-1.35-3-3-3zm-4.5-1.5c0-2.48 2.02-4.5 4.5-4.5s4.5 2.02 4.5 4.5v1.5h1.5c1.65 0 3 1.35 3 3v9c0 1.65-1.35 3-3 3h-12c-1.65 0-3-1.35-3-3v-9c0-1.65 1.35-3 3-3h1.5v-1.5z' fill='%239ca3af'/%3E%3C/svg%3E";

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Section Title */}
      <h2 className="text-lg md:text-xl font-bold text-gray-800 mb-4">
        {t('select_product_title')}
      </h2>

      {/* Product List */}
      {productItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {t('no_products_available')}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {productItems.map((item) => (
            <button
              key={item.id}
              onClick={() => onProductItemSelect(item.id)}
              className={`flex flex-col items-center justify-between p-4 rounded-lg border-2 transition-all ${
                selectedProductItem === item.id
                  ? 'border-primary bg-green-50'
                  : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
              }`}
            >
              {/* Product Icon & Name */}
              <div className="flex flex-col items-center gap-2 mb-2">
                <img
                  src={item.iconUrl || fallbackIcon}
                  alt={item.name}
                  className="w-12 h-12 object-contain rounded"
                  onError={(e) => {
                    const img = e.target as HTMLImageElement;
                    if (img.src !== fallbackIcon) {
                      img.src = fallbackIcon;
                    }
                  }}
                />
                <span className="font-medium text-gray-800 text-center text-sm">
                  {item.name}
                </span>
              </div>

              {/* Price */}
              <span className="font-bold text-primary text-sm">
                {formatPrice(item.price)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductSelector;
